// src/pages/hr/Attendance.js
// =====================================================
// FuMind_GM HR Module - Phase 3E Attendance Page
// Break-aware attendance: manual check-in, optional breaks, live work timer
// =====================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getEmployeeMasterByEmail, getEmployeeMasters } from "../../services/employeeService";
import {
  checkInAttendance,
  checkOutAttendance,
  getAllAttendanceRecords,
  getAttendanceByEmployee,
  getAttendanceToday,
  getTodayAttendanceContext,
  startAttendanceBreak,
  endAttendanceBreak,
  submitAttendanceRegularization,
  actionAttendanceRegularization
} from "../../services/attendanceService";

import { displayValue } from "../../utils/displayValue";

import { getCurrentUser } from '../../utils/userContext';



import "../../styles/hrPageShell.css";
const ATTENDANCE_STATUSES = [
  "Not Marked",
  "Checked In",
  "Checked Out",
  "Present",
  "Absent",
  "Late",
  "Half Day",
  "On Leave",
  "Holiday",
  "Weekly Off",
  "Regularization Required",
  "Missed Checkout",
  "Missed Resume",
  "Regularized"
];

const COUNTRIES = ["", "India", "Germany", "Global / Other"];

function Attendance() {
 
   const currentUserObj = getCurrentUser();
   const currentUserEmail = currentUserObj?.workEmail || "";
   const currentRole = currentUserObj?.role || currentUserObj?.Role || currentUserObj?.selectedRole || currentUserObj?.currentRole || "Employee";
   const isApproverRole = ["Admin", "HRAdmin", "HR", "Manager"].includes(currentRole);
  const [employee, setEmployee] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [allRecords, setAllRecords] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const [locationText, setLocationText] = useState("Office");
  const [remarks, setRemarks] = useState("");

  const [historyFromDate, setHistoryFromDate] = useState("");
  const [historyToDate, setHistoryToDate] = useState("");
  const [managerFromDate, setManagerFromDate] = useState("");
  const [managerToDate, setManagerToDate] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeIdFilter, setEmployeeIdFilter] = useState("");
  const [regularizationRemarksById, setRegularizationRemarksById] = useState({});
  const [regularizationActionSavingId, setRegularizationActionSavingId] = useState("");

  // Break / live timer state. This is intentionally employee-controlled.
  const [nowTick, setNowTick] = useState(Date.now());
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakStartedAt, setBreakStartedAt] = useState(null);
  const [breakSessions, setBreakSessions] = useState([]);
  const [persistedBreakMinutes, setPersistedBreakMinutes] = useState(0);
  const [breakEndOverride, setBreakEndOverride] = useState(false);
  const [frozenActiveWorkSeconds, setFrozenActiveWorkSeconds] = useState(null);
  const [breakStartOverrideUtc, setBreakStartOverrideUtc] = useState(null);

  const getValue = useCallback((record, ...keys) => {
    if (!record) return "";

    for (const key of keys) {
      const value = record[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        if (typeof value === "object") return value.Value || value.value || value.label || value.Label || "";
        return value;
      }
    }

    const recordKeys = Object.keys(record);
    for (const key of keys) {
      const foundKey = recordKeys.find((recordKey) => recordKey.toLowerCase() === String(key).toLowerCase());
      if (foundKey) {
        const value = record[foundKey];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          if (typeof value === "object") return value.Value || value.value || value.label || value.Label || "";
          return value;
        }
      }
    }

    return "";
  }, []);

  const getDisplay = useCallback((record, ...keys) => displayValue(getValue(record, ...keys)), [getValue]);
  const normalizeText = useCallback((value) => String(displayValue(value) || "").trim().toLowerCase(), []);
  const isSameText = useCallback((value, expected) => normalizeText(value) === normalizeText(expected), [normalizeText]);
  const isTruthyValue = useCallback((value) => {
    if (value === true) return true;
    if (value === false || value === null || value === undefined) return false;
    return ["true", "yes", "1"].includes(normalizeText(value));
  }, [normalizeText]);


  const getCountryFromTimeZone = useCallback((tz = "") => {
    const zone = String(tz || "").toLowerCase();
    if (zone.includes("kolkata") || zone.includes("calcutta")) return "India";
    if (zone.includes("berlin") || zone.includes("europe/")) return "Germany";
    return "";
  }, []);

  const getEmployeeName = useCallback((record) => {
    const fullName = getDisplay(record, "fullName", "FullName", "employeeName", "EmployeeName", "displayName", "DisplayName");
    const firstName = getDisplay(record, "firstName", "FirstName");
    const lastName = getDisplay(record, "lastName", "LastName");
    const combinedName = `${firstName || ""} ${lastName || ""}`.trim();
    return fullName && fullName !== "-" ? fullName : combinedName;
  }, [getDisplay]);

  const normalizeAttendanceRecord = useCallback((value) => {
    if (!value) return null;
    if (Array.isArray(value)) return value.length > 0 ? value[0] : null;
    if (value.attendance) return normalizeAttendanceRecord(value.attendance);
    if (value.attendanceRecord) return normalizeAttendanceRecord(value.attendanceRecord);
    if (value.record) return normalizeAttendanceRecord(value.record);
    if (value.value && Array.isArray(value.value)) return value.value.length > 0 ? value.value[0] : null;
    if (value.success === true && value.attendance === null) return null;
    return value;
  }, []);

  const employeeId = getDisplay(employee, "employeeId", "EmployeeId", "EmpId");
  const workEmail = getDisplay(employee, "workEmail", "WorkEmail") || currentUserEmail;
  const timeZone = getDisplay(employee, "timeZone", "TimeZone") || "Asia/Kolkata";
  const employeeName = getEmployeeName(employee);
  const workCountry = getDisplay(employee, "workCountry", "WorkCountry", "country", "Country") || getCountryFromTimeZone(timeZone) || "-";
  const workLocation = getDisplay(employee, "workLocationId", "WorkLocationId", "locationCode", "LocationCode", "location", "Location") || "-";
  const employmentStatus = getDisplay(employee, "employmentStatus", "EmploymentStatus");
  const onboardingStage = getDisplay(employee, "onboardingStage", "OnboardingStage");
  const isActiveEmployee = isSameText(employmentStatus, "Active") && isSameText(onboardingStage, "Active");

  const todayContext = useMemo(() => getTodayAttendanceContext(timeZone), [timeZone]);
  const normalizedTodayAttendance = useMemo(() => normalizeAttendanceRecord(todayAttendance), [todayAttendance, normalizeAttendanceRecord]);
  const todayAttendanceCountry = getCountryFromTimeZone(timeZone) || getDisplay(normalizedTodayAttendance, "country", "Country") || workCountry;

  const attendanceStatus = getDisplay(normalizedTodayAttendance, "attendanceStatus", "AttendanceStatus") || "Not Marked";
  const attendanceStatusKey = normalizeText(attendanceStatus);
  const checkInValue = getDisplay(normalizedTodayAttendance, "utcCheckInTime", "UtcCheckInTime", "localCheckInTime", "LocalCheckInTime");
  const checkOutValue = getDisplay(normalizedTodayAttendance, "utcCheckOutTime", "UtcCheckOutTime", "localCheckOutTime", "LocalCheckOutTime");

  const hasCheckedIn = Boolean(checkInValue && checkInValue !== "-") || ["checked in", "present", "checked out"].includes(attendanceStatusKey);
  const hasCheckedOut = Boolean(checkOutValue && checkOutValue !== "-") || ["present", "checked out"].includes(attendanceStatusKey);

  const canCheckIn = isActiveEmployee && !hasCheckedIn && !hasCheckedOut && !saving;
  const canStartBreak = isActiveEmployee && hasCheckedIn && !hasCheckedOut && !isOnBreak && !saving;
  const canResumeWork = isActiveEmployee && hasCheckedIn && !hasCheckedOut && isOnBreak && !saving;
  const canCheckOut = isActiveEmployee && hasCheckedIn && !hasCheckedOut && !isOnBreak && !saving;

  const buttonReason = !isActiveEmployee
    ? "Employee status/stage is not Active / Active"
    : hasCheckedOut
      ? "Attendance already completed"
      : hasCheckedIn
        ? isOnBreak ? "Break in progress - click Resume Work" : "Check-out pending"
        : "Ready for check-in";

  useEffect(() => {
    if (!hasCheckedIn || hasCheckedOut) return undefined;

    const timerId = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(timerId);
  }, [hasCheckedIn, hasCheckedOut]);

  const parseDateValue = (value, assumeUtc = false) => {
    if (!value || value === "-") return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const text = String(value).trim();
    if (!text) return null;

    const hasTimeZoneSuffix = /([zZ]|[+-]\d{2}:?\d{2})$/.test(text);
    let normalizedText = text;

    // yyyy-MM-dd HH:mm:ss -> yyyy-MM-ddTHH:mm:ss
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(normalizedText)) {
      normalizedText = normalizedText.replace(" ", "T");
    }

    // MM/dd/yyyy HH:mm:ss -> yyyy-MM-ddTHH:mm:ss
    const slashMatch = normalizedText.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}:\d{2}(?::\d{2})?)/);
    if (slashMatch) {
      const [, mm, dd, yyyy, time] = slashMatch;
      normalizedText = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}T${time}`;
    }

    if (assumeUtc && !hasTimeZoneSuffix) {
      normalizedText = `${normalizedText}Z`;
    }

    const parsed = new Date(normalizedText);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  const checkInDate = useMemo(() => parseDateValue(checkInValue, true), [checkInValue]);

  // Break_End flow persists completed break minutes. React only calculates the currently active break from currentBreakStartUtc.
  const previousBreakSeconds = Math.max(0, (Number(persistedBreakMinutes) || 0) * 60);

  const currentBreakSeconds = useMemo(() => {
    if (!isOnBreak || !breakStartedAt) return 0;

    const start = parseDateValue(breakStartedAt, true);
    if (!start) return 0;

    const rawBreakSeconds = Math.max(0, Math.floor((nowTick - start.getTime()) / 1000));

    // Guardrail: current break cannot exceed elapsed time since check-in.
    if (checkInDate) {
      const grossFromCheckIn = Math.max(0, Math.floor((nowTick - checkInDate.getTime()) / 1000));
      return Math.min(rawBreakSeconds, grossFromCheckIn);
    }

    return rawBreakSeconds;
  }, [breakStartedAt, checkInDate, isOnBreak, nowTick]);

  const totalBreakSeconds = previousBreakSeconds + currentBreakSeconds;

  const grossSecondsSinceCheckIn = useMemo(() => {
    if (!checkInDate || hasCheckedOut) return 0;
    return Math.max(0, Math.floor((nowTick - checkInDate.getTime()) / 1000));
  }, [checkInDate, hasCheckedOut, nowTick]);

  const calculatedActiveWorkSeconds = Math.max(0, grossSecondsSinceCheckIn - totalBreakSeconds);
  const activeWorkSeconds = isOnBreak && frozenActiveWorkSeconds !== null
    ? Math.max(0, Number(frozenActiveWorkSeconds) || 0)
    : calculatedActiveWorkSeconds;

  const totalBreakMinutes = Math.round(totalBreakSeconds / 60);

  const formatDuration = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const hrs = Math.floor(safeSeconds / 3600);
    const mins = Math.floor((safeSeconds % 3600) / 60);
    const secs = safeSeconds % 60;
    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const formatDateTime = (value) => {
    const text = displayValue(value);
    if (!text || text === "-") return "-";
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return text;
    return parsed.toLocaleString("en-IN", {
      timeZone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };


  const formatHoursFromSeconds = (seconds) => {
    const safeSeconds = Math.max(0, Math.round(Number(seconds) || 0));
    const totalMinutes = Math.round(safeSeconds / 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const decimalHours = totalMinutes / 60;
    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")} (${decimalHours.toFixed(2)} h)`;
  };

  const parseWorkHoursToSeconds = (value) => {
    const text = displayValue(value);
    if (!text || text === "-") return null;
    const hhmm = text.match(/^(\d{1,3}):(\d{2})(?::(\d{2}))?/);
    if (hhmm) {
      return (Number(hhmm[1]) * 3600) + (Number(hhmm[2]) * 60) + (Number(hhmm[3] || 0));
    }
    const decimal = Number(String(text).replace(/[^0-9.]/g, ""));
    return Number.isFinite(decimal) ? Math.round(decimal * 3600) : null;
  };

  const getRecordDateKey = (row) => {
    const rawDate = getDisplay(row, "localAttendanceDate", "LocalAttendanceDate", "attendanceDate", "AttendanceDate", "date", "Date");
    if (!rawDate || rawDate === "-") return "";
    const parsed = parseDateValue(rawDate, false);
    if (parsed) return parsed.toISOString().slice(0, 10);
    const text = String(rawDate).trim();
    const isoMatch = text.match(/\d{4}-\d{2}-\d{2}/);
    if (isoMatch) return isoMatch[0];
    return text;
  };

  const isRecordInsideDateRange = (row, startDate, endDate) => {
    const key = getRecordDateKey(row);
    if (!key) return true;
    if (startDate && key < startDate) return false;
    if (endDate && key > endDate) return false;
    return true;
  };

  const getNetWorkSecondsFromRecord = (row) => {
    const checkInUtc = getDisplay(row, "utcCheckInTime", "UtcCheckInTime");
    const checkOutUtc = getDisplay(row, "utcCheckOutTime", "UtcCheckOutTime");
    const checkInLocal = getDisplay(row, "localCheckInTime", "LocalCheckInTime");
    const checkOutLocal = getDisplay(row, "localCheckOutTime", "LocalCheckOutTime");
    const checkIn = parseDateValue(checkInUtc, true) || parseDateValue(checkInLocal, false);
    const checkOut = parseDateValue(checkOutUtc, true) || parseDateValue(checkOutLocal, false);
    const breakMinutes = Number(getDisplay(row, "breakMinutes", "BreakMinutes")) || 0;

    if (checkIn && checkOut && checkOut >= checkIn) {
      return Math.max(0, Math.round((checkOut.getTime() - checkIn.getTime()) / 1000) - (breakMinutes * 60));
    }
    return parseWorkHoursToSeconds(getDisplay(row, "workHours", "WorkHours"));
  };

  const formatRecordWorkHours = (row) => {
    const seconds = getNetWorkSecondsFromRecord(row);
    if (seconds === null || seconds === undefined) return getDisplay(row, "workHours", "WorkHours") || "-";
    return formatHoursFromSeconds(seconds);
  };

  const mergeEmployeeFromGetAllIfNeeded = useCallback(async (emp) => {
    const empStatus = getDisplay(emp, "employmentStatus", "EmploymentStatus");
    const empStage = getDisplay(emp, "onboardingStage", "OnboardingStage");

    if (empStatus && empStatus !== "-" && empStage && empStage !== "-") return emp;

    try {
      const allEmployees = await getEmployeeMasters();
      const empEmail = getDisplay(emp, "workEmail", "WorkEmail").toLowerCase();
      const empId = getDisplay(emp, "employeeId", "EmployeeId", "EmpId").toLowerCase();

      const masterRow = (allEmployees || []).find((row) => {
        const rowEmail = getDisplay(row, "workEmail", "WorkEmail").toLowerCase();
        const rowId = getDisplay(row, "employeeId", "EmployeeId", "EmpId").toLowerCase();
        return (empEmail && rowEmail === empEmail) || (empId && rowId === empId);
      });

      if (!masterRow) return emp;

      return {
        ...emp,
        ...masterRow,
        employmentStatus: getDisplay(masterRow, "employmentStatus", "EmploymentStatus") || emp.employmentStatus,
        onboardingStage: getDisplay(masterRow, "onboardingStage", "OnboardingStage") || emp.onboardingStage,
        country: getDisplay(masterRow, "country", "Country") || emp.country,
        workLocationId: getDisplay(masterRow, "workLocationId", "WorkLocationId") || emp.workLocationId,
        timeZone: getDisplay(masterRow, "timeZone", "TimeZone") || emp.timeZone
      };
    } catch (error) {
      console.warn("Attendance EmployeeMaster GetAll merge failed. Using GetByEmail only.", error);
      return emp;
    }
  }, [getDisplay]);

  const resetLocalBreakState = () => {
    setIsOnBreak(false);
    setBreakStartedAt(null);
    setBreakSessions([]);
    setPersistedBreakMinutes(0);
    setBreakEndOverride(false);
    setBreakStartOverrideUtc(null);
    setFrozenActiveWorkSeconds(null);
    setNowTick(Date.now());
  };

  const loadTodayAttendance = useCallback(async (emp) => {
    if (!emp) return;

    const empId = getDisplay(emp, "employeeId", "EmployeeId", "EmpId");
    const empEmail = getDisplay(emp, "workEmail", "WorkEmail") || currentUserEmail;
    const empTimeZone = getDisplay(emp, "timeZone", "TimeZone") || "Asia/Kolkata";
    const context = getTodayAttendanceContext(empTimeZone);

    const data = await getAttendanceToday({
      employeeId: empId,
      workEmail: empEmail,
      localAttendanceDate: context.localAttendanceDate,
      timeZone: empTimeZone
    });

    const attendanceRecord = normalizeAttendanceRecord(data);
    console.log("HR_Attendance_Get_Today response:", data, "normalized:", attendanceRecord);
    setTodayAttendance(attendanceRecord);

    if (!attendanceRecord) {
      resetLocalBreakState();
      return;
    }

    const serverBreakMinutes = Number(getDisplay(attendanceRecord, "breakMinutes", "BreakMinutes")) || 0;
    const serverBreakStartUtc = getDisplay(attendanceRecord, "currentBreakStartUtc", "CurrentBreakStartUtc");
    const serverBreakStartLocal = getDisplay(attendanceRecord, "currentBreakStartLocal", "CurrentBreakStartLocal");
    const serverIsOnBreak = isTruthyValue(getValue(attendanceRecord, "isOnBreak", "IsOnBreak"));
    const serverCheckInUtc = getDisplay(attendanceRecord, "utcCheckInTime", "UtcCheckInTime", "localCheckInTime", "LocalCheckInTime");
    const serverCheckOutUtc = getDisplay(attendanceRecord, "utcCheckOutTime", "UtcCheckOutTime", "localCheckOutTime", "LocalCheckOutTime");
    const recordHasCheckedOut = Boolean(serverCheckOutUtc && serverCheckOutUtc !== "-");

    setPersistedBreakMinutes(serverBreakMinutes);

    if (!breakEndOverride && serverIsOnBreak && !recordHasCheckedOut) {
      const effectiveServerBreakStartUtc = breakStartOverrideUtc || serverBreakStartUtc;
      const utcBreakDate = parseDateValue(effectiveServerBreakStartUtc, true);
      const localBreakDate = parseDateValue(serverBreakStartLocal, false);
      const restoredCheckInDate = parseDateValue(serverCheckInUtc, true);

      let restoredBreakStart = utcBreakDate ? effectiveServerBreakStartUtc : localBreakDate ? serverBreakStartLocal : new Date().toISOString();
      let restoredBreakDate = parseDateValue(restoredBreakStart, Boolean(utcBreakDate));

      if (restoredBreakDate && restoredCheckInDate && restoredBreakDate.getTime() < restoredCheckInDate.getTime()) {
        restoredBreakStart = serverCheckInUtc;
        restoredBreakDate = restoredCheckInDate;
      }

      const activeBeforeBreak = restoredBreakDate && restoredCheckInDate
        ? Math.max(0, Math.floor((restoredBreakDate.getTime() - restoredCheckInDate.getTime()) / 1000) - (serverBreakMinutes * 60))
        : 0;

      setFrozenActiveWorkSeconds(activeBeforeBreak);
      setIsOnBreak(true);
      setBreakStartedAt(restoredBreakStart);
    } else {
      setFrozenActiveWorkSeconds(null);
      setBreakStartOverrideUtc(null);
      setIsOnBreak(false);
      setBreakStartedAt(null);
    }

    setNowTick(Date.now());
  }, [breakEndOverride, breakStartOverrideUtc, currentUserEmail, getDisplay, getValue, isTruthyValue, normalizeAttendanceRecord]);

  const loadEmployeeHistory = useCallback(async (emp = employee) => {
    if (!emp) return;

    const empId = getDisplay(emp, "employeeId", "EmployeeId", "EmpId");
    const empEmail = getDisplay(emp, "workEmail", "WorkEmail") || currentUserEmail;

    let rows = await getAttendanceByEmployee({
      employeeId: empId,
      workEmail: empEmail,
      fromDate: historyFromDate,
      toDate: historyToDate
    });

    // Fallback: if employee-specific flow returns empty due to filter/date issue,
    // use Get All and filter locally. This also helps during flow transition.
    if ((!rows || rows.length === 0) && (historyFromDate || historyToDate)) {
      const allRows = await getAllAttendanceRecords({
        fromDate: historyFromDate,
        toDate: historyToDate,
        employeeId: empId
      });
      rows = (allRows || []).filter((row) => {
        const rowEmpId = getDisplay(row, "employeeId", "EmployeeId", "EmpId").toLowerCase();
        const rowEmail = getDisplay(row, "workEmail", "WorkEmail").toLowerCase();
        return (empId && rowEmpId === empId.toLowerCase()) || (empEmail && rowEmail === empEmail.toLowerCase());
      });
    }

    setEmployeeHistory((rows || []).filter((row) => isRecordInsideDateRange(row, historyFromDate, historyToDate)));
  }, [currentUserEmail, employee, getDisplay, historyFromDate, historyToDate]);

  const loadProfileAndAttendance = useCallback(async () => {
    try {
      setLoading(true);
      setMessage("");

      if (!currentUserEmail) {
        throw new Error("Please login to load attendance.");
      }

      const result = await getEmployeeMasterByEmail(currentUserEmail);
      let emp = Array.isArray(result.employee) ? result.employee[0] : result.employee;
      if (!emp) throw new Error("Employee profile not found.");

      emp = await mergeEmployeeFromGetAllIfNeeded(emp);
      console.log("Attendance employee after merge:", emp);
      setEmployee(emp);
      await loadTodayAttendance(emp);
      // Load own attendance history immediately so Employee Regularization Request is visible without manually clicking Load History.
      try {
        const empId = getDisplay(emp, "employeeId", "EmployeeId", "EmpId");
        const empEmail = getDisplay(emp, "workEmail", "WorkEmail") || currentUserEmail;
        const rows = await getAttendanceByEmployee({ employeeId: empId, workEmail: empEmail, fromDate: historyFromDate, toDate: historyToDate });
        setEmployeeHistory((rows || []).filter((row) => isRecordInsideDateRange(row, historyFromDate, historyToDate)));
      } catch (historyError) {
        console.warn("Attendance initial history load failed.", historyError);
        setEmployeeHistory([]);
      }
    } catch (error) {
      console.error("Attendance load error:", error);
      setMessage(error.message || "Failed to load attendance.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  }, [currentUserEmail, getDisplay, historyFromDate, historyToDate, loadTodayAttendance, mergeEmployeeFromGetAllIfNeeded]);

  useEffect(() => {
    loadProfileAndAttendance();
  }, [loadProfileAndAttendance]);

  const handleCheckIn = async () => {
    if (!canCheckIn) return;

    try {
      setSaving(true);
      setMessage("");
      resetLocalBreakState();

      const result = await checkInAttendance({
        employeeId,
        workEmail,
        localAttendanceDate: todayContext.localAttendanceDate,
        timeZone,
        checkInSource: "Web",
        locationText
      });

      setMessage(result?.message || "Check-in successful.");
      setMessageType("success");

      const returnedAttendance = normalizeAttendanceRecord(result);
      if (returnedAttendance) setTodayAttendance(returnedAttendance);
      await loadTodayAttendance(employee);
    } catch (error) {
      console.error("Check-in error:", error);
      setMessage(error.message || "Failed to check in.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const handleStartBreak = async () => {
    if (!canStartBreak) return;

    try {
      setSaving(true);
      setMessage("");

      const result = await startAttendanceBreak({
        employeeId,
        workEmail,
        localAttendanceDate: todayContext.localAttendanceDate,
        timeZone,
        breakStartSource: "Web",
        remarks: "Break started from Attendance page."
      });

      const startUtcFromFlow = result?.breakStartUtc || new Date().toISOString();
      setBreakEndOverride(false);
      setBreakStartOverrideUtc(startUtcFromFlow);
      setFrozenActiveWorkSeconds(calculatedActiveWorkSeconds);
      setBreakStartedAt(startUtcFromFlow);
      setIsOnBreak(true);
      setNowTick(Date.now());

      setTodayAttendance((prev) => ({
        ...(prev || normalizedTodayAttendance || {}),
        isOnBreak: true,
        currentBreakStartUtc: startUtcFromFlow,
        currentBreakStartLocal: result?.breakStartLocal || getDisplay(prev || normalizedTodayAttendance, "currentBreakStartLocal", "CurrentBreakStartLocal") || ""
      }));

      await loadTodayAttendance(employee);
      setMessage(result?.message || "Break started. Work timer is paused.");
      setMessageType("success");
    } catch (error) {
      console.error("Start break error:", error);
      setMessage(error.message || "Failed to start break.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };
  const handleResumeWork = async () => {
    if (!canResumeWork) return;

    try {
      setSaving(true);
      setMessage("");

      const result = await endAttendanceBreak({
        employeeId,
        workEmail,
        localAttendanceDate: todayContext.localAttendanceDate,
        timeZone,
        breakEndSource: "Web",
        remarks: "Break ended from Attendance page."
      });

      const totalBreakFromFlow = Number(result?.totalBreakMinutes ?? result?.breakMinutes ?? persistedBreakMinutes) || 0;

      // Important: Break_End response is the source of truth immediately after clicking Resume Work.
      // Even if Get Today returns stale currentBreakStartUtc for a moment, do not keep UI on break.
      setBreakEndOverride(true);
      setBreakStartOverrideUtc(null);
      setBreakStartedAt(null);
      setIsOnBreak(false);
      setFrozenActiveWorkSeconds(null);
      setPersistedBreakMinutes(totalBreakFromFlow);
      setNowTick(Date.now());

      setTodayAttendance((prev) => ({
        ...(prev || normalizedTodayAttendance || {}),
        isOnBreak: false,
        currentBreakStartUtc: "",
        currentBreakStartLocal: "",
        breakMinutes: totalBreakFromFlow,
        breakDetails: result?.breakDetails || getDisplay(prev || normalizedTodayAttendance, "breakDetails", "BreakDetails") || ""
      }));

      setMessage(result?.message || "Break ended. Work timer resumed.");
      setMessageType("success");

      // Refresh after local state update. breakEndOverride prevents stale server values from re-opening break state.
      await loadTodayAttendance(employee);
    } catch (error) {
      console.error("End break error:", error);
      setMessage(error.message || "Failed to end break.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const buildBreakPayload = () => {
    // With Break_Start / Break_End flows, completed break time is already persisted in SharePoint.
    // Checkout should send the latest displayed total. Checkout is disabled while break is active.
    const sessions = [...breakSessions];

    return {
      sessions,
      breakMinutes: totalBreakMinutes,
      breakDetails: JSON.stringify(sessions)
    };
  };

  const handleCheckOut = async () => {
    if (!canCheckOut) return;

    try {
      setSaving(true);
      setMessage("");

      const breakPayload = buildBreakPayload();

      const result = await checkOutAttendance({
        employeeId,
        workEmail,
        localAttendanceDate: todayContext.localAttendanceDate,
        timeZone,
        checkOutSource: "Web",
        remarks,
        breakMinutes: breakPayload.breakMinutes,
        breakDetails: breakPayload.breakDetails
      });

      setMessage(result?.message || "Check-out successful.");
      setMessageType("success");
      const returnedAttendance = normalizeAttendanceRecord(result);
      if (returnedAttendance) setTodayAttendance(returnedAttendance);
      setBreakSessions(breakPayload.sessions);
      setBreakStartOverrideUtc(null);
      setBreakStartedAt(null);
      setIsOnBreak(false);
      await loadTodayAttendance(employee);
    } catch (error) {
      console.error("Check-out error:", error);
      setMessage(error.message || "Failed to check out.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const handleLoadAllRecords = async () => {
    try {
      setLoading(true);
      setMessage("");

      const rows = await getAllAttendanceRecords({
        fromDate: managerFromDate,
        toDate: managerToDate,
        country: countryFilter,
        employeeId: employeeIdFilter,
        attendanceStatus: statusFilter
      });

      setAllRecords(rows || []);
    } catch (error) {
      console.error("Attendance all records load error:", error);
      setMessage(error.message || "Failed to load all attendance records.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const filteredAllRecords = useMemo(() => {
    return allRecords.filter((row) => {
      const countryValue = getDisplay(row, "country", "Country");
      const statusValue = getDisplay(row, "attendanceStatus", "AttendanceStatus");
      const rowEmployeeId = getDisplay(row, "employeeId", "EmployeeId", "EmpId").toLowerCase();
      const countryMatch = !countryFilter || normalizeText(countryValue) === normalizeText(countryFilter);
      const statusMatch = !statusFilter || normalizeText(statusValue) === normalizeText(statusFilter);
      const employeeMatch = !employeeIdFilter || rowEmployeeId.includes(employeeIdFilter.toLowerCase());
      const dateMatch = isRecordInsideDateRange(row, managerFromDate, managerToDate);
      return countryMatch && statusMatch && employeeMatch && dateMatch;
    });
  }, [allRecords, countryFilter, employeeIdFilter, getDisplay, managerFromDate, managerToDate, normalizeText, statusFilter]);


  const getAttendanceRowId = (row) => getDisplay(row, "id", "ID") || getDisplay(row, "attendanceId", "AttendanceId");

  const isRegularizationRecord = (row) => {
    const status = normalizeText(getDisplay(row, "attendanceStatus", "AttendanceStatus"));
    const approval = normalizeText(getDisplay(row, "approvalStatus", "ApprovalStatus"));
    const regularizationStatus = normalizeText(getDisplay(row, "regularizationStatus", "RegularizationStatus"));
    return isTruthyValue(getValue(row, "regularizationRequired", "RegularizationRequired")) ||
      regularizationStatus === "pending" ||
      regularizationStatus === "submitted" ||
      approval.includes("regularization") ||
      status === "regularization required" ||
      status === "missed checkout" ||
      status === "missed resume";
  };

  const regularizationRows = useMemo(() => {
    const sourceRows = isApproverRole ? filteredAllRecords : employeeHistory;
    return (sourceRows || []).filter((row) => isRegularizationRecord(row));
  }, [employeeHistory, filteredAllRecords, isApproverRole]);

  const getRegularizationStatusText = useCallback((row) => {
    return getDisplay(row, "regularizationStatus", "RegularizationStatus") || "Pending";
  }, [getDisplay]);

  const isRegularizationSubmittedOrClosed = useCallback((row) => {
    const status = normalizeText(getRegularizationStatusText(row));
    return ["submitted", "approved", "rejected"].includes(status);
  }, [getRegularizationStatusText, normalizeText]);

  const getEmployeeRegularizationExplanation = useCallback((row) => {
    return getDisplay(row, "regularizationRemarks", "RegularizationRemarks") ||
      getDisplay(row, "employeeRegularizationRemarks", "EmployeeRegularizationRemarks") ||
      getDisplay(row, "employeeRemarks", "EmployeeRemarks") ||
      getDisplay(row, "remarks", "Remarks") ||
      "-";
  }, [getDisplay]);

  const getRegularizationReason = useCallback((row) => {
    return getDisplay(row, "autoCloseReason", "AutoCloseReason") ||
      getDisplay(row, "regularizationReason", "RegularizationReason") ||
      getDisplay(row, "reason", "Reason") ||
      getDisplay(row, "attendanceStatus", "AttendanceStatus") ||
      "-";
  }, [getDisplay]);

  const applyLocalRegularizationUpdate = useCallback((row, action, remarksForRow) => {
    const rowId = getAttendanceRowId(row);
    const nextStatus = action === "submitRegularization"
      ? "Submitted"
      : action === "approveRegularization"
        ? "Approved"
        : action === "rejectRegularization"
          ? "Rejected"
          : getRegularizationStatusText(row);
    const patchRow = (item) => {
      const itemId = getAttendanceRowId(item);
      if (itemId !== rowId) return item;
      return {
        ...item,
        regularizationStatus: nextStatus,
        RegularizationStatus: nextStatus,
        regularizationRemarks: remarksForRow,
        RegularizationRemarks: remarksForRow,
        approvalStatus: action === "submitRegularization" ? "Pending" : nextStatus,
        ApprovalStatus: action === "submitRegularization" ? "Pending" : nextStatus,
        regularizationRequired: action === "submitRegularization" ? true : action === "approveRegularization" || action === "rejectRegularization" ? false : item.regularizationRequired,
      };
    };
    setEmployeeHistory((previous) => (previous || []).map(patchRow));
    setAllRecords((previous) => (previous || []).map(patchRow));
  }, [getAttendanceRowId, getRegularizationStatusText]);

  const handleRegularizationRemarksChange = (row, value) => {
    const rowId = getAttendanceRowId(row);
    setRegularizationRemarksById((previous) => ({ ...previous, [rowId]: value }));
  };

  const handleRegularizationAction = async (row, action) => {
    const rowId = getAttendanceRowId(row);
    const defaultRemark = action === "approveRegularization"
      ? "Regularization approved from Attendance page."
      : action === "rejectRegularization"
        ? "Regularization rejected from Attendance page."
        : "Regularization submitted from Attendance page.";
    const remarksForRow = regularizationRemarksById[rowId] || defaultRemark;

    try {
      setRegularizationActionSavingId(rowId);
      setMessage("");
      await actionAttendanceRegularization({
        id: getDisplay(row, "id", "ID"),
        attendanceId: getDisplay(row, "attendanceId", "AttendanceId"),
        employeeId: getDisplay(row, "employeeId", "EmployeeId", "EmpId"),
        action,
        actionBy: currentUserEmail || workEmail,
        actionByRole: action === "submitRegularization" ? "Employee" : "Manager",
        remarks: remarksForRow
      });
      applyLocalRegularizationUpdate(row, action, remarksForRow);
      setMessage(action === "approveRegularization" ? "Regularization approved successfully." : action === "rejectRegularization" ? "Regularization rejected successfully." : "Regularization submitted successfully.");
      setMessageType("success");
      await handleLoadAllRecords();
      await loadEmployeeHistory(employee);
      await loadTodayAttendance(employee);
    } catch (error) {
      console.error("Regularization action error:", error);
      setMessage(error.message || "Failed to update regularization.");
      setMessageType("error");
    } finally {
      setRegularizationActionSavingId("");
    }
  };

  const renderRegularizationRows = () => {
    if (!regularizationRows || regularizationRows.length === 0) {
      return (
        <tr>
          <td style={emptyCellStyle} colSpan="12">{isApproverRole ? "No pending regularization records loaded." : "No regularization requests found in My Attendance History."}</td>
        </tr>
      );
    }

    return regularizationRows.map((row, index) => {
      const rowId = getAttendanceRowId(row) || index;
      const savingThisRow = regularizationActionSavingId === rowId;
      const regularizationStatus = getRegularizationStatusText(row);
      const alreadySubmittedOrClosed = isRegularizationSubmittedOrClosed(row);
      const employeeExplanation = getEmployeeRegularizationExplanation(row);
      const regularizationReason = getRegularizationReason(row);
      return (
        <tr key={rowId}>
          <td style={tdStyle}>{getDisplay(row, "localAttendanceDate", "LocalAttendanceDate") || "-"}</td>
          <td style={tdStyle}>{getDisplay(row, "employeeId", "EmployeeId") || "-"}</td>
          <td style={tdStyle}>{getDisplay(row, "workEmail", "WorkEmail") || "-"}</td>
          <td style={tdStyle}>{getDisplay(row, "attendanceStatus", "AttendanceStatus") || "-"}</td>
          <td style={tdStyle}>{regularizationStatus}</td>
          <td style={tdStyle}>{regularizationReason}</td>
          <td style={tdStyle}>{getDisplay(row, "localCheckInTime", "LocalCheckInTime") || "-"}</td>
          <td style={tdStyle}>{getDisplay(row, "localCheckOutTime", "LocalCheckOutTime") || "-"}</td>
          <td style={tdStyle}>{formatRecordWorkHours(row)}</td>
          <td style={tdStyle}>{employeeExplanation}</td>
          <td style={tdStyle}>
            <input
              placeholder={isApproverRole ? "Manager approval remarks" : "Employee explanation"}
              value={regularizationRemarksById[rowId] || (!isApproverRole && alreadySubmittedOrClosed ? employeeExplanation : "")}
              onChange={(event) => handleRegularizationRemarksChange(row, event.target.value)}
              disabled={!isApproverRole && alreadySubmittedOrClosed}
              style={{ ...inputStyle, minWidth: "220px", backgroundColor: (!isApproverRole && alreadySubmittedOrClosed) ? "#f8fafc" : "#ffffff" }}
            />
          </td>
          <td style={tdStyle}>
            <div style={buttonRowStyle}>
              {!isApproverRole && (
                <button type="button" style={alreadySubmittedOrClosed ? disabledButtonStyle : secondaryButtonStyle} disabled={savingThisRow || alreadySubmittedOrClosed} onClick={() => handleRegularizationAction(row, "submitRegularization")}>{alreadySubmittedOrClosed ? regularizationStatus : "Submit Explanation"}</button>
              )}
              {isApproverRole && (
                <>
                  <button type="button" style={regularizationStatus === "Approved" || regularizationStatus === "Rejected" ? disabledButtonStyle : activateButtonStyle} disabled={savingThisRow || regularizationStatus === "Approved" || regularizationStatus === "Rejected"} onClick={() => handleRegularizationAction(row, "approveRegularization")}>Approve</button>
                  <button type="button" style={regularizationStatus === "Approved" || regularizationStatus === "Rejected" ? disabledButtonStyle : warningButtonStyle} disabled={savingThisRow || regularizationStatus === "Approved" || regularizationStatus === "Rejected"} onClick={() => handleRegularizationAction(row, "rejectRegularization")}>Reject</button>
                </>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  const renderRecordRows = (records, emptyText) => {
    if (!records || records.length === 0) {
      return (
        <tr>
          <td style={emptyCellStyle} colSpan="10">{emptyText}</td>
        </tr>
      );
    }

    return records.map((row, index) => {
      const key = getDisplay(row, "id", "ID") || getDisplay(row, "attendanceId", "AttendanceId") || index;
      const status = getDisplay(row, "attendanceStatus", "AttendanceStatus") || "-";

      return (
        <tr key={key}>
          <td style={tdStyle}>{getDisplay(row, "localAttendanceDate", "LocalAttendanceDate") || "-"}</td>
          <td style={tdStyle}>{getDisplay(row, "employeeId", "EmployeeId") || "-"}</td>
          <td style={tdStyle}>{getDisplay(row, "workEmail", "WorkEmail") || "-"}</td>
          <td style={tdStyle}>{status}</td>
          <td style={tdStyle}>{getDisplay(row, "localCheckInTime", "LocalCheckInTime") || formatDateTime(getDisplay(row, "utcCheckInTime", "UtcCheckInTime"))}</td>
          <td style={tdStyle}>{getDisplay(row, "localCheckOutTime", "LocalCheckOutTime") || formatDateTime(getDisplay(row, "utcCheckOutTime", "UtcCheckOutTime"))}</td>
          <td style={tdStyle}>{formatRecordWorkHours(row)}</td>
          <td style={tdStyle}>{getDisplay(row, "breakMinutes", "BreakMinutes") || "0"}</td>
          <td style={tdStyle}>{getCountryFromTimeZone(getDisplay(row, "timeZone", "TimeZone")) || getDisplay(row, "workCountry", "WorkCountry", "country", "Country") || "-"}</td>
          <td style={tdStyle}>{getDisplay(row, "timeZone", "TimeZone") || "-"}</td>
        </tr>
      );
    });
  };

  return (
    <div className="fm-hr-page-shell" style={pageStyle}>
      <div className="fm-hr-hero" style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Attendance</h1>
          <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.88)" }}>
            Employee-controlled check-in, optional breaks, and live work-hour tracking.
          </p>
        </div>
        <button type="button" style={secondaryButtonStyle} onClick={loadProfileAndAttendance} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {message && <div style={messageType === "error" ? errorMessageStyle : successMessageStyle}>{message}</div>}

      <div style={kpiGridStyle}>
        <div style={cardStyle}>
          <div style={labelTextStyle}>Today</div>
          <div style={largeTextStyle}>{todayContext.localAttendanceDate}</div>
          <div style={smallTextStyle}>{timeZone}</div>
        </div>
        <div style={cardStyle}>
          <div style={labelTextStyle}>Status</div>
          <div style={largeTextStyle}>{isOnBreak ? "On Break" : hasCheckedIn && attendanceStatus === "Not Marked" ? "Checked In" : attendanceStatus}</div>
          <div style={smallTextStyle}>{isActiveEmployee ? "Employee Active" : "Attendance locked until employee is active"}</div>
        </div>
        <div style={cardStyle}>
          <div style={labelTextStyle}>Live Work Timer</div>
          <div style={largeTextStyle}>{hasCheckedOut ? getDisplay(normalizedTodayAttendance, "workHours", "WorkHours") || "0" : formatDuration(activeWorkSeconds)}</div>
          <div style={smallTextStyle}>{isOnBreak ? "Frozen during break" : hasCheckedIn ? "Running after check-in" : "Starts after Check In"}</div>
        </div>
        <div style={cardStyle}>
          <div style={labelTextStyle}>Break Time</div>
          <div style={largeTextStyle}>{formatDuration(totalBreakSeconds)}</div>
          <div style={smallTextStyle}>{totalBreakMinutes} minutes will be sent at checkout</div>
        </div>
      </div>

      <div style={layoutStyle}>
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>Today's Attendance</h2>
          <div style={metaGridStyle}>
            <div><strong>Employee Name:</strong> {employeeName || "-"}</div>
            <div><strong>Employee Code:</strong> {employeeId || "-"}</div>
            <div><strong>Email:</strong> {workEmail || "-"}</div>
            <div><strong>Work Country:</strong> {todayAttendanceCountry || "-"}</div>
            <div><strong>Work Location:</strong> {workLocation}</div>
            <div><strong>Time Zone:</strong> {timeZone}</div>
            <div><strong>Check In:</strong> {getDisplay(normalizedTodayAttendance, "localCheckInTime", "LocalCheckInTime") || "-"}</div>
            <div><strong>Check Out:</strong> {getDisplay(normalizedTodayAttendance, "localCheckOutTime", "LocalCheckOutTime") || "-"}</div>
            <div><strong>Status:</strong> {isOnBreak ? "On Break" : hasCheckedIn && attendanceStatus === "Not Marked" ? "Checked In" : attendanceStatus}</div>
            <div><strong>Employee Stage:</strong> {employmentStatus || "-"} / {onboardingStage || "-"}</div>
          </div>
          <div style={formRowStyle}>
            <label style={fieldStyle}>
              Location / Work Mode
              <input value={locationText} onChange={(event) => setLocationText(event.target.value)} style={inputStyle} disabled={!canCheckIn} />
            </label>
            <label style={fieldStyle}>
              Check-out Remarks
              <input value={remarks} onChange={(event) => setRemarks(event.target.value)} style={inputStyle} disabled={!canCheckOut} />
            </label>
          </div>

          <div style={timerPanelStyle}>
            <div>
              <div style={labelTextStyle}>Active Work</div>
              <div style={timerTextStyle}>{hasCheckedOut ? "Stopped" : formatDuration(activeWorkSeconds)}</div>
            </div>
            <div>
              <div style={labelTextStyle}>Break</div>
              <div style={timerTextStyle}>{formatDuration(totalBreakSeconds)}</div>
            </div>
          </div>

          <div style={buttonRowStyle}>
            <button type="button" style={canCheckIn ? primaryButtonStyle : disabledButtonStyle} onClick={handleCheckIn} disabled={!canCheckIn}>
              Check In
            </button>
            <button type="button" style={canStartBreak ? warningButtonStyle : disabledButtonStyle} onClick={handleStartBreak} disabled={!canStartBreak}>
              Start Break
            </button>
            <button type="button" style={canResumeWork ? primaryButtonStyle : disabledButtonStyle} onClick={handleResumeWork} disabled={!canResumeWork}>
              Resume Work
            </button>
            <button type="button" style={canCheckOut ? activateButtonStyle : disabledButtonStyle} onClick={handleCheckOut} disabled={!canCheckOut}>
              {hasCheckedOut ? "Attendance Completed" : hasCheckedIn ? "Check Out" : "Check Out Locked"}
            </button>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>My Attendance History</h2>
          <p style={{ marginTop: 0, color: "rgba(255,255,255,0.88)" }}>Load your history to review previous attendance and submit regularization explanations when required.</p>
          <div style={filterRowStyle}>
            <input type="date" value={historyFromDate} onChange={(event) => setHistoryFromDate(event.target.value)} style={inputStyle} />
            <input type="date" value={historyToDate} onChange={(event) => setHistoryToDate(event.target.value)} style={inputStyle} />
            <button type="button" style={secondaryButtonStyle} onClick={() => loadEmployeeHistory(employee)} disabled={loading}>
              Load History
            </button>
          </div>
          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Employee</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Check In</th>
                  <th style={thStyle}>Check Out</th>
                  <th style={thStyle}>Hours</th>
                  <th style={thStyle}>Break Min</th>
                  <th style={thStyle}>Work Country</th>
                  <th style={thStyle}>Time Zone</th>
                </tr>
              </thead>
              <tbody>{renderRecordRows(employeeHistory, "No attendance history found.")}</tbody>
            </table>
          </div>
        </div>
      </div>

      {isApproverRole && <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>HR / Manager Attendance View</h2>
        <div style={filterGridStyle}>
          <input type="date" value={managerFromDate} onChange={(event) => setManagerFromDate(event.target.value)} style={inputStyle} />
          <input type="date" value={managerToDate} onChange={(event) => setManagerToDate(event.target.value)} style={inputStyle} />
          <select value={countryFilter} onChange={(event) => setCountryFilter(event.target.value)} style={inputStyle}>
            {COUNTRIES.map((country) => <option key={country || "all"} value={country}>{country || "All Countries"}</option>)}
          </select>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={inputStyle}>
            <option value="">All Statuses</option>
            {ATTENDANCE_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <input placeholder="Employee ID" value={employeeIdFilter} onChange={(event) => setEmployeeIdFilter(event.target.value)} style={inputStyle} />
          <button type="button" style={secondaryButtonStyle} onClick={handleLoadAllRecords} disabled={loading}>Load All</button>
        </div>
        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Check In</th>
                <th style={thStyle}>Check Out</th>
                <th style={thStyle}>Hours</th>
                <th style={thStyle}>Break Min</th>
                <th style={thStyle}>Work Country</th>
                <th style={thStyle}>Time Zone</th>
              </tr>
            </thead>
            <tbody>{renderRecordRows(filteredAllRecords, "No attendance records loaded.")}</tbody>
          </table>
        </div>
      </div>}

      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>{isApproverRole ? "Attendance Regularization Approval" : "My Attendance Regularization Request"}</h2>
        <p style={{ marginTop: 0, color: "rgba(255,255,255,0.88)" }}>
          {isApproverRole
            ? "Shows auto-closed, missed checkout, missed resume, and pending regularization records from the loaded HR/Manager results. Click Load All above to refresh this queue."
            : "Shows your own missed checkout, missed resume, early checkout, long break, and pending regularization records. This section loads automatically and also refreshes when you load My Attendance History."}
        </p>
        <div style={tableWrapperStyle}>
          <table style={{ ...tableStyle, minWidth: "1280px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Attendance Status</th>
                <th style={thStyle}>Regularization</th>
                <th style={thStyle}>Reason</th>
                <th style={thStyle}>Check In</th>
                <th style={thStyle}>Check Out</th>
                <th style={thStyle}>Hours</th>
                <th style={thStyle}>Employee Explanation</th>
                <th style={thStyle}>{isApproverRole ? "Manager Remarks" : "Your Explanation"}</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>{renderRegularizationRows()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const pageStyle = { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", backgroundColor: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" };
const headerStyle = { display: "flex", justifyContent: "space-between", gap: "18px", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "16px", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" };
const cardStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", marginBottom: "14px", minWidth: 0, overflow: "hidden" };
const kpiGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "11px", marginBottom: "14px", minWidth: 0 };
const layoutStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "14px", alignItems: "start", minWidth: 0 };
const metaGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px", color: "var(--fm-text)", fontSize: "13px", marginBottom: "14px" };
const formRowStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", marginBottom: "14px" };
const filterRowStyle = { display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "14px", minWidth: 0 };
const filterGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px", marginBottom: "14px", minWidth: 0 };
const fieldStyle = { display: "grid", gap: "6px", fontWeight: 700, color: "var(--fm-text)", fontSize: "12.5px", minWidth: 0 };
const inputStyle = { height: "40px", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "8px 10px", fontSize: "13px", backgroundColor: "var(--fm-card)", boxSizing: "border-box", width: "100%", maxWidth: "100%", minWidth: 0 };
const buttonRowStyle = { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" };
const primaryButtonStyle = { backgroundColor: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const activateButtonStyle = { backgroundColor: "#047857", color: "#ffffff", border: "1px solid #047857", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const warningButtonStyle = { backgroundColor: "#b45309", color: "#ffffff", border: "1px solid #b45309", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const secondaryButtonStyle = { backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const disabledButtonStyle = { backgroundColor: "#94a3b8", color: "#ffffff", border: "1px solid #94a3b8", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "not-allowed", fontSize: "12.5px" };
const labelTextStyle = { color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 750 };
const largeTextStyle = { color: "var(--fm-page-accent)", fontSize: "24px", fontWeight: 800, marginTop: "4px", lineHeight: 1.15, overflowWrap: "anywhere" };
const smallTextStyle = { color: "var(--fm-muted)", fontSize: "12px", marginTop: "4px" };
const timerPanelStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px", border: "1px solid var(--fm-border)", borderRadius: "14px", padding: "14px", backgroundColor: "#f8fbff", marginBottom: "16px" };
const timerTextStyle = { color: "var(--fm-page-accent)", fontSize: "22px", fontWeight: 800, marginTop: "4px", overflowWrap: "anywhere" };
const tableWrapperStyle = { width: "100%", maxWidth: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch", border: "1px solid var(--fm-border)", borderRadius: "14px" };
const tableStyle = { width: "100%", minWidth: "980px", borderCollapse: "collapse", fontSize: "12.5px" };
const thStyle = { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", backgroundColor: "#f8fafc", color: "var(--fm-text)", whiteSpace: "nowrap", fontWeight: 800 };
const tdStyle = { borderBottom: "1px solid var(--fm-border)", padding: "10px", color: "var(--fm-text)", verticalAlign: "top", whiteSpace: "nowrap" };
const emptyCellStyle = { padding: "18px", textAlign: "center", color: "var(--fm-muted)" };
const successMessageStyle = { border: "1px solid #bbf7d0", backgroundColor: "#ecfdf5", color: "#047857", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const errorMessageStyle = { border: "1px solid #fecaca", backgroundColor: "#fef2f2", color: "#b91c1c", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
export default Attendance;
